import React, { useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { FaLock, FaEye, FaEyeSlash, FaCheck, FaTimes } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    hasLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    // Check password strength for new password
    if (name === "newPassword") {
      setPasswordStrength({
        hasLength: value.length >= 8,
        hasUppercase: /[A-Z]/.test(value),
        hasLowercase: /[a-z]/.test(value),
        hasNumber: /[0-9]/.test(value),
        hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(value),
      });
    }

    // Clear confirm password error when typing
    if (name === "confirmPassword" && errors.confirmPassword) {
      setErrors({ ...errors, confirmPassword: "" });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = "Current password is required";
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (!/(?=.*[A-Z])/.test(formData.newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one uppercase letter";
    } else if (!/(?=.*[a-z])/.test(formData.newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one lowercase letter";
    } else if (!/(?=.*[0-9])/.test(formData.newPassword)) {
      newErrors.newPassword = "Password must contain at least one number";
    } else if (!/(?=.*[!@#$%^&*(),.?":{}|<>])/.test(formData.newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one special character";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (
      formData.oldPassword &&
      formData.newPassword &&
      formData.oldPassword === formData.newPassword
    ) {
      newErrors.newPassword =
        "New password must be different from current password";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);

      // Simulate API call
      setTimeout(() => {
        console.log("Password changed successfully:", {
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
        });
        setIsLoading(false);
        setIsSuccess(true);

        // Reset form after 3 seconds and redirect to login
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }, 1500);
    } else {
      setErrors(newErrors);
    }
  };

  const getPasswordStrength = () => {
    const strength = Object.values(passwordStrength).filter(Boolean).length;
    if (strength <= 2) return { text: "Weak", color: "#e53e3e", width: "20%" };
    if (strength <= 3) return { text: "Fair", color: "#ed8936", width: "40%" };
    if (strength <= 4) return { text: "Good", color: "#ecc94b", width: "70%" };
    return { text: "Strong", color: "#4CAF50", width: "100%" };
  };

  if (isSuccess) {
    return (
      <TheChangePassword>
        <div className="change-container">
          <div className="change-card success-card">
            <div className="success-icon">✓</div>
            <h2>Password Changed Successfully!</h2>
            <p>
              Your password has been updated. You will be redirected to login in
              a few seconds.
            </p>
            <Link to="/login" className="login-link">
              Go to Login Now
            </Link>
          </div>
        </div>
      </TheChangePassword>
    );
  }

  return (
    <>
      <Helmet>
        <title>Change Password | Trans World Shipping</title>
        <meta
          name="description"
          content="Change your password for Trans World Shipping account. Keep your account secure with a strong password."
        />
        <link
          rel="canonical"
          href="https://transworldshippingcompany.com/change-password"
        />
      </Helmet>

      <TheChangePassword>
        <div className="change-container">
          <div className="change-card">
            <div className="change-header">
              <h1>Change Password</h1>
              <p>Keep your account secure with a strong password</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="oldPassword">
                  Current Password <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <FaLock className="input-icon" />
                  <input
                    type={showPasswords.old ? "text" : "password"}
                    id="oldPassword"
                    name="oldPassword"
                    value={formData.oldPassword}
                    onChange={handleChange}
                    placeholder="Enter your current password"
                    className={errors.oldPassword ? "error" : ""}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility("old")}
                  >
                    {showPasswords.old ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.oldPassword && (
                  <span className="error-message">{errors.oldPassword}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">
                  New Password <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <FaLock className="input-icon" />
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Create a new password"
                    className={errors.newPassword ? "error" : ""}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility("new")}
                  >
                    {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.newPassword && (
                  <span className="error-message">{errors.newPassword}</span>
                )}

                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <div className="password-strength">
                    <div className="strength-bar">
                      <div
                        className="strength-fill"
                        style={{
                          width: getPasswordStrength().width,
                          backgroundColor: getPasswordStrength().color,
                        }}
                      ></div>
                    </div>
                    <div
                      className="strength-text"
                      style={{ color: getPasswordStrength().color }}
                    >
                      Password Strength: {getPasswordStrength().text}
                    </div>
                    <div className="strength-requirements">
                      <p
                        className={
                          passwordStrength.hasLength ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasLength ? <FaCheck /> : <FaTimes />}{" "}
                        At least 8 characters
                      </p>
                      <p
                        className={
                          passwordStrength.hasUppercase ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasUppercase ? (
                          <FaCheck />
                        ) : (
                          <FaTimes />
                        )}{" "}
                        At least one uppercase letter
                      </p>
                      <p
                        className={
                          passwordStrength.hasLowercase ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasLowercase ? (
                          <FaCheck />
                        ) : (
                          <FaTimes />
                        )}{" "}
                        At least one lowercase letter
                      </p>
                      <p
                        className={
                          passwordStrength.hasNumber ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasNumber ? <FaCheck /> : <FaTimes />}{" "}
                        At least one number
                      </p>
                      <p
                        className={
                          passwordStrength.hasSpecial ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasSpecial ? (
                          <FaCheck />
                        ) : (
                          <FaTimes />
                        )}{" "}
                        At least one special character
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">
                  Confirm New Password <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <FaLock className="input-icon" />
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your new password"
                    className={errors.confirmPassword ? "error" : ""}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility("confirm")}
                  >
                    {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="error-message">
                    {errors.confirmPassword}
                  </span>
                )}
                {formData.confirmPassword &&
                  formData.newPassword === formData.confirmPassword &&
                  formData.newPassword && (
                    <span className="success-message">✓ Passwords match</span>
                  )}
              </div>

              <button type="submit" className="change-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Updating Password...
                  </>
                ) : (
                  "Update Password"
                )}
              </button>
            </form>

            <div className="help-section">
              <Link to="/forgot-password" className="forgot-link">
                Forgot your current password?
              </Link>
            </div>
          </div>
        </div>
      </TheChangePassword>
    </>
  );
};

const TheChangePassword = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  .change-container {
    width: 100%;
    max-width: 550px;
    margin: 0 auto;
  }

  .change-card {
    background: white;
    border-radius: 20px;
    padding: 2.5rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }
  }

  .success-card {
    text-align: center;

    .success-icon {
      width: 80px;
      height: 80px;
      background: #4caf50;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 3rem;
      margin: 0 auto 1.5rem;
      animation: scaleIn 0.3s ease;
    }

    @keyframes scaleIn {
      from {
        transform: scale(0);
      }
      to {
        transform: scale(1);
      }
    }

    h2 {
      font-size: 1.5rem;
      color: #0b1e33;
      margin-bottom: 1rem;
      font-weight: 700;
    }

    p {
      color: #718096;
      margin-bottom: 2rem;
      line-height: 1.6;
    }

    .login-link {
      display: inline-block;
      background: #d4af37;
      color: #0b1e33;
      text-decoration: none;
      padding: 0.75rem 2rem;
      border-radius: 10px;
      font-weight: 600;
      transition: all 0.3s ease;

      &:hover {
        background: #c4a12e;
        transform: translateY(-2px);
      }
    }
  }

  .change-header {
    text-align: center;
    margin-bottom: 2rem;

    h1 {
      font-size: 1.8rem;
      color: #0b1e33;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 0.9rem;
      color: #718096;
    }
  }

  .form-group {
    margin-bottom: 1.5rem;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: #0b1e33;
      font-size: 0.9rem;

      .required {
        color: #e53e3e;
        margin-left: 2px;
      }
    }

    .input-wrapper {
      position: relative;

      .input-icon {
        position: absolute;
        left: 1rem;
        top: 50%;
        transform: translateY(-50%);
        color: #a0aec0;
        font-size: 1rem;
        pointer-events: none;
        z-index: 1;
      }

      input {
        width: 100%;
        padding: 0.85rem 1rem 0.85rem 2.75rem;
        border: 1.5px solid #e2e8f0;
        border-radius: 10px;
        font-size: 1rem;
        transition: all 0.3s ease;
        font-family: inherit;

        &:focus {
          outline: none;
          border-color: #d4af37;
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }

        &.error {
          border-color: #e53e3e;

          &:focus {
            box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
          }
        }

        &:disabled {
          background: #f7fafc;
          cursor: not-allowed;
        }
      }

      .password-toggle {
        position: absolute;
        right: 1rem;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        cursor: pointer;
        color: #a0aec0;
        font-size: 1rem;
        display: flex;
        align-items: center;
        padding: 0;
        z-index: 1;

        &:hover {
          color: #d4af37;
        }
      }
    }

    .error-message {
      display: block;
      margin-top: 0.5rem;
      font-size: 0.8rem;
      color: #e53e3e;
    }

    .success-message {
      display: block;
      margin-top: 0.5rem;
      font-size: 0.8rem;
      color: #4caf50;
    }
  }

  .password-strength {
    margin-top: 0.75rem;

    .strength-bar {
      height: 4px;
      background: #e2e8f0;
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 0.5rem;

      .strength-fill {
        height: 100%;
        transition: width 0.3s ease;
      }
    }

    .strength-text {
      font-size: 0.8rem;
      font-weight: 600;
      margin-bottom: 0.75rem;
    }

    .strength-requirements {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.5rem;
      padding: 0.75rem;
      background: #f7fafc;
      border-radius: 8px;

      p {
        font-size: 0.75rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;

        &.valid {
          color: #4caf50;
        }

        &.invalid {
          color: #e53e3e;
        }

        svg {
          font-size: 0.7rem;
        }
      }
    }
  }

  .change-btn {
    width: 100%;
    background: #d4af37;
    color: #0b1e33;
    border: none;
    padding: 0.85rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    margin-bottom: 1.5rem;

    &:hover {
      background: #c4a12e;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
    }

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }

    .spinner {
      display: inline-block;
      width: 18px;
      height: 18px;
      border: 2px solid #0b1e33;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
      margin-right: 0.5rem;
      vertical-align: middle;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .help-section {
    text-align: center;

    .forgot-link {
      color: #d4af37;
      text-decoration: none;
      font-size: 0.9rem;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media screen and (max-width: 600px) {
    padding: 1rem;

    .change-card {
      padding: 1.5rem;
    }

    .change-header h1 {
      font-size: 1.5rem;
    }

    .strength-requirements {
      grid-template-columns: 1fr !important;
    }
  }

  @media screen and (max-width: 480px) {
    .change-card {
      padding: 1.25rem;
    }

    .change-header h1 {
      font-size: 1.3rem;
    }
  }
`;

export default ChangePassword;
